/**
 * moonshot-split-guards.js
 *
 * Closes the bypasses that would let a customer skip the cart-page
 * split UI and land at /checkout with a mixed cart:
 *
 *   1. Cart-drawer Checkout button — when cart is mixed, intercept the
 *      submit and redirect to /cart so the customer sees the split flow.
 *   2. PDP Buy Now / Shop Pay / dynamic checkout buttons — hide them
 *      whenever the cart is mixed OR the customer's PDP selection
 *      would create a mixed cart. Forces them through Add to Cart →
 *      /cart → split CTA.
 *
 * Strategy:
 *   - On page load + after any cart mutation, fetch /cart.js, analyze
 *     with MoonshotSplit.analyzeCart(), toggle html.moonshot-cart-mixed.
 *   - Inject site-wide CSS that hides the bypass buttons under that
 *     class.
 *   - Listen for the PDP fulfillment-radio change so the buttons update
 *     when the customer picks a different fulfillment for the item
 *     they're about to add.
 *
 * The cart-validation Function is still the backstop, but the
 * customer-facing UX never lets them get into the bad state in the
 * first place when this file loads.
 */
(function () {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const MIXED_CLASS = "moonshot-cart-mixed";
  const STYLE_ID = "moonshot-split-guards-styles";

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      /* Hide the bypass buttons whenever the cart is in a mixed state.
         Native Checkout button must go through /cart so we can render
         the split overview. Buy Now / Shop Pay / Apple Pay all bypass
         /cart by design — they're disabled here so we don't end up at
         /checkout with a mixed cart. */
      html.${MIXED_CLASS} button[name="checkout"]:not(.moonshot-split-allowed),
      html.${MIXED_CLASS} .additional-checkout-buttons,
      html.${MIXED_CLASS} .shopify-payment-button,
      html.${MIXED_CLASS} [data-shopify="payment-button"],
      html.${MIXED_CLASS} .product-form__buttons button[name="add"][formaction*="/checkouts"],
      html.${MIXED_CLASS} [data-moonshot-buy-now] {
        display: none !important;
      }
      /* PDP-specific: when the currently-selected fulfillment radio
         would create a mixed cart, hide Buy Now for THIS product only. */
      .product-form.moonshot-pdp-would-mix .additional-checkout-buttons,
      .product-form.moonshot-pdp-would-mix .shopify-payment-button,
      .product-form.moonshot-pdp-would-mix [data-shopify="payment-button"],
      .product-form.moonshot-pdp-would-mix [data-moonshot-buy-now] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  async function fetchCart() {
    try {
      const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  function analyze(cart) {
    if (window.MoonshotSplit && window.MoonshotSplit.analyzeCart) {
      return window.MoonshotSplit.analyzeCart(cart);
    }
    return { isMixed: false, hasShip: false, hasPickup: false, pickupGroup: null };
  }

  async function refreshCartState() {
    const cart = await fetchCart();
    const analysis = cart ? analyze(cart) : null;
    const isMixed = !!(analysis && analysis.isMixed);
    document.documentElement.classList.toggle(MIXED_CLASS, isMixed);
    // Re-evaluate PDP would-mix state since cart mode may have changed.
    evaluatePdpFulfillmentSelection(analysis);
    return analysis;
  }

  // ─────────────────────────────────────────────────────────────────────
  // PDP guard — when the customer is on a PDP and selects a fulfillment
  // that would mix with the existing cart, mark the form so Buy Now is
  // hidden for THIS product (Add to Cart stays usable).
  // ─────────────────────────────────────────────────────────────────────
  let lastCartAnalysis = null;

  function evaluatePdpFulfillmentSelection(analysis) {
    if (analysis) lastCartAnalysis = analysis;
    const a = lastCartAnalysis;
    if (!a) return;
    const forms = document.querySelectorAll(".product-form, [data-product-form]");
    forms.forEach(function (form) {
      const radio = form.querySelector(
        "[data-moonshot-fulfillment-input]:checked",
      ) || document.querySelector("[data-moonshot-fulfillment-input]:checked");
      const method = radio && radio.getAttribute("data-method");
      let wouldMix = false;
      if (method) {
        const isShipChoice = method === "ship";
        const isPickupChoice = method.indexOf("pickup:") === 0;
        if (isShipChoice && a.hasPickup) wouldMix = true;
        if (isPickupChoice && a.hasShip) wouldMix = true;
        // Different pickup group than the cart's existing pickup also conflicts
        if (
          isPickupChoice &&
          a.hasPickup &&
          a.pickupGroup &&
          method !== "pickup:" + a.pickupGroup
        ) {
          wouldMix = true;
        }
      }
      form.classList.toggle("moonshot-pdp-would-mix", wouldMix);
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // Drawer guard — intercept the cart-drawer Checkout submit, fetch the
  // live cart state, and route appropriately:
  //   - Mixed cart  → /cart (split UI takes over)
  //   - Uniform     → /checkout (skip the cart page, customer's intent
  //                              was express-checkout)
  // Doesn't rely on the moonshot-cart-mixed body class being set in
  // time (Warehouse doesn't reliably fire add-to-cart events we listen
  // to). Always checks at click time.
  // ─────────────────────────────────────────────────────────────────────
  document.addEventListener(
    "submit",
    function (e) {
      const form = e.target;
      if (!form || !form.action || form.action.indexOf("/cart") === -1) return;
      const submitter = e.submitter;
      if (!submitter || submitter.name !== "checkout") return;
      // Always intercept; decide where to go async.
      e.preventDefault();
      (async function () {
        const cart = await fetchCart();
        const analysis = cart ? analyze(cart) : { isMixed: false };
        if (analysis && analysis.isMixed) {
          window.location.href = "/cart";
        } else {
          window.location.href = "/checkout";
        }
      })();
    },
    true,
  );

  // ─────────────────────────────────────────────────────────────────────
  // Re-evaluate after cart mutations. Listen for the events the theme
  // already fires + observe storage / fetch in a lightweight way.
  // ─────────────────────────────────────────────────────────────────────
  function bindCartChangeListeners() {
    // Warehouse + custom events used elsewhere in the theme:
    [
      "cart:refresh",
      "cart:updated",
      "cart:add-item",
      "cart:item-added",
      "cart:item-removed",
    ].forEach(function (evt) {
      document.documentElement.addEventListener(evt, refreshCartState);
      document.addEventListener(evt, refreshCartState);
    });
    // PDP fulfillment radio changes
    document.addEventListener("change", function (e) {
      const t = e.target;
      if (t && t.matches && t.matches("[data-moonshot-fulfillment-input]")) {
        evaluatePdpFulfillmentSelection(null);
      }
    });
  }

  function init() {
    injectStyles();
    bindCartChangeListeners();
    refreshCartState();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
